// Super reactions (#87): a new reaction that arrives with an effect (zoom, spin or poof) plays it once on every device
// that has the message on screen. The setting is saved to the account like channel activity (/api/me/settings), and
// in localStorage so it applies before the account loads. The effects themselves are live only: the room sends one
// with the reaction event and never stores it, and this module forgets it once it has played.
import { useSyncExternalStore } from "react";
import type { ReactionEffect } from "@bloom/shared";
import { api } from "./api.ts";
import { playThud } from "./jumboSound.ts";

const KEY = "bloom:reaction-effects";
const listeners = new Set<() => void>();
const notify = () => {
  for (const fn of listeners) fn();
};
const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

function stored(): boolean {
  try {
    return localStorage.getItem(KEY) !== "off";
  } catch {
    return true;
  }
}

let on = stored();

/** Sets it on this device right away; `save` also stores it on the account for my other devices. */
export function setReactionEffects(next: boolean, { save = true } = {}) {
  on = next;
  try {
    localStorage.setItem(KEY, next ? "on" : "off");
  } catch {
    // private mode: still applies for this page
  }
  notify();
  if (save) void api("/me/settings", { method: "PUT", body: { reactionEffects: next } }).catch(() => {});
}

export const getReactionEffects = () => on;
export const useReactionEffects = (): boolean => useSyncExternalStore(subscribe, () => on);

/** How long an effect counts as playing: the longest one (spin) and its glow, with room to spare. */
const PLAY_MS = 3000;

export interface PlayingEffect {
  effect: ReactionEffect;
  /** Counts up, so a second effect on the same chip is a new key and restarts the animation. */
  serial: number;
}

/** The effects playing now, by message and then emoji. */
let playing = new Map<string, Map<string, PlayingEffect>>();
let serial = 0;
const NONE: ReadonlyMap<string, PlayingEffect> = new Map();

/** A reaction event with an effect arrived: play it on that chip, unless effects are off. */
export function playReactionEffect(messageId: string, emoji: string, effect: ReactionEffect) {
  if (!on) return;
  const mine = ++serial;
  playing = new Map(playing).set(messageId, new Map(playing.get(messageId)).set(emoji, { effect, serial: mine }));
  notify();
  setTimeout(() => {
    if (playing.get(messageId)?.get(emoji)?.serial !== mine) return;
    const left = new Map(playing.get(messageId));
    left.delete(emoji);
    playing = new Map(playing);
    if (left.size) playing.set(messageId, left);
    else playing.delete(messageId);
    notify();
  }, PLAY_MS);
}

/**
 * The effects a message's reaction chips play now, by emoji. Each is gone once it has played, so scrolling back or
 * reopening never replays it. The same map comes back until that message's effects change, so other rows don't redraw.
 */
export function useReactionEffectsOn(messageId: string): ReadonlyMap<string, PlayingEffect> {
  return useSyncExternalStore(subscribe, () => playing.get(messageId) ?? NONE);
}

// Jumbo reactions (#714): every one lands with a thud. Like a super effect it is live only: the reaction event says
// `jumbo`, this module plays it once on the devices that have the message on screen, and it never replays from
// history, on scroll or on a reconnect. It replaces the super effect, so a jumbo reaction never zooms, spins or poofs.
// Reaction effects off: no thud, no sound, no drop. Reduced motion: the CSS leaves out the drop; the sound stays.

/** The landing: the emoji drops, squashes and bounces (styles.css .jumbo-land) while the message jolts. */
export const LAND_MS = 520;
/** The emoji hits the counter this far into the landing (0 to 1); the thud sounds then. */
export const LAND_IMPACT = 0.35;

/** The landings in progress, by message and then emoji; the value counts up so a second landing restarts the animation. */
let landing = new Map<string, Map<string, number>>();
let landSerial = 0;
const NO_LANDINGS: ReadonlyMap<string, number> = new Map();

/** A jumbo reaction arrived (live): land it on that counter, unless effects are off. */
export function playJumboLanding(messageId: string, emoji: string) {
  if (!on) return;
  const mine = ++landSerial;
  landing = new Map(landing).set(messageId, new Map(landing.get(messageId)).set(emoji, mine));
  notify();
  setTimeout(() => {
    if (landing.get(messageId)?.get(emoji) !== mine) return;
    const left = new Map(landing.get(messageId));
    left.delete(emoji);
    landing = new Map(landing);
    if (left.size) landing.set(messageId, left);
    else landing.delete(messageId);
    notify();
  }, LAND_MS + 100);
}

/** The landings a message's counters play now, by emoji. The same map comes back until they change. */
export const jumboLandingsOf = (messageId: string): ReadonlyMap<string, number> => landing.get(messageId) ?? NO_LANDINGS;
export function useJumboLandings(messageId: string): ReadonlyMap<string, number> {
  return useSyncExternalStore(subscribe, () => jumboLandingsOf(messageId));
}

/** One message's counter for one emoji thuds at most once in this long, so a script that removes and re-adds jumbo is not a noise machine. */
export const THUD_WINDOW_MS = 2000;

const thudded = new Set<number>();
/** When each message and emoji last thudded here, in ms. */
const lastThud = new Map<string, number>();
/**
 * The thud for one landing, at its impact. A row that is on screen calls this; the landing sounds once however many
 * lists draw the message, and not at all in a hidden tab. A landing inside THUD_WINDOW_MS of the last thud for the same
 * message and emoji is silent (it still drops and jolts). `now` is the clock, for tests.
 */
export function thudForLanding(messageId: string, emoji: string, serial: number, now: number = Date.now()) {
  if (thudded.has(serial) || (typeof document !== "undefined" && document.hidden)) return;
  thudded.add(serial);
  if (thudded.size > 50) thudded.delete(thudded.values().next().value as number);
  const key = `${messageId}\u0000${emoji}`;
  const last = lastThud.get(key);
  if (last !== undefined && now - last >= 0 && now - last < THUD_WINDOW_MS) return;
  lastThud.delete(key);
  lastThud.set(key, now);
  if (lastThud.size > 200) lastThud.delete(lastThud.keys().next().value as string);
  playThud((LAND_MS * LAND_IMPACT) / 1000);
}
